import { PageContainer } from '@/components/Atoms'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/Card'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { createFileRoute } from '@tanstack/react-router'
import {
  ArrowLeftRightIcon,
  BuildingIcon,
  CalculatorIcon,
  ClockIcon,
  FileTextIcon,
  HandshakeIcon,
  MapPinIcon,
  ReceiptIcon,
  ShieldCheckIcon,
  UsersIcon,
  UserStarIcon,
  ListIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { AssumptionTile } from './-components/AssumptionTile'
import { SapImportTile } from './-components/SapImportTile'

export const Route = createFileRoute('/_auth/_modules/credit/operations/')({
  component: CreditOperations,
})

function CreditOperations() {
  const { t } = useTranslation('credit')

  return (
    <PageContainer className="flex flex-col gap-6">
      <PermissionsGuard
        requiredOneOf={[
          'credit_multiplier_view',
          'credit_province_aov_view',
          'credit_very_low_risk_customers_view',
          'credit_order_cutoff_view',
        ]}
      >
        <Card>
          <CardHeader>
            <CardTitle>{t('assumptions.title')}</CardTitle>
            <CardDescription>{t('assumptions.description')}</CardDescription>
          </CardHeader>

          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <PermissionsGuard required={['credit_multiplier_view']}>
                <AssumptionTile
                  to="/credit/operations/multiplier"
                  icon={<CalculatorIcon />}
                  title={t('assumptions.multiplier.title')}
                  description={t('assumptions.multiplier.description')}
                />
              </PermissionsGuard>

              <PermissionsGuard required={['credit_province_aov_view']}>
                <AssumptionTile
                  to="/credit/operations/province-aov-per-carton"
                  icon={<MapPinIcon />}
                  title={t('assumptions.provinceAovPerCarton.title')}
                  description={t('assumptions.provinceAovPerCarton.description')}
                />
              </PermissionsGuard>

              <PermissionsGuard required={['credit_very_low_risk_customers_view']}>
                <AssumptionTile
                  to="/credit/operations/very-low-risk-customers"
                  icon={<UserStarIcon />}
                  title={t('veryLowRiskCustomers.tile.title')}
                  description={t('veryLowRiskCustomers.tile.description')}
                />
              </PermissionsGuard>

              <PermissionsGuard required={['credit_order_cutoff_view']}>
                <AssumptionTile
                  to="/credit/operations/order-cutoffs"
                  icon={<ClockIcon />}
                  title={t('provinceOrderCutoffs.tile.title')}
                  description={t('provinceOrderCutoffs.tile.description')}
                />
              </PermissionsGuard>
            </div>
          </CardContent>
        </Card>
      </PermissionsGuard>

      <Card>
        <CardHeader>
          <CardTitle>{t('sapImports.title')}</CardTitle>
          <CardDescription>{t('sapImports.description')}</CardDescription>
        </CardHeader>

        <CardContent>
          <PermissionsGuard required={['credit_sap_imports_create']}>
            <div className="grid grid-cols-2 gap-4">
              <SapImportTile
                uploadType="import-sap-customer"
                icon={<UsersIcon />}
                title={t('sapImports.customer.title')}
                description={t('sapImports.customer.description')}
              />
              <SapImportTile
                uploadType="import-sap-customer-company"
                icon={<BuildingIcon />}
                title={t('sapImports.customerCompany.title')}
                description={t('sapImports.customerCompany.description')}
              />
              <SapImportTile
                uploadType="import-sap-partner-functions"
                icon={<HandshakeIcon />}
                title={t('sapImports.partnerFunctions.title')}
                description={t('sapImports.partnerFunctions.description')}
              />
              <SapImportTile
                uploadType="import-sap-sales-orders"
                icon={<ReceiptIcon />}
                title={t('sapImports.salesOrders.title')}
                description={t('sapImports.salesOrders.description')}
              />
              <SapImportTile
                uploadType="import-sap-billing-documents"
                icon={<FileTextIcon />}
                title={t('sapImports.billingDocuments.title')}
                description={t('sapImports.billingDocuments.description')}
              />
              <SapImportTile
                uploadType="import-sap-billing-document-details"
                icon={<ListIcon />}
                title={t('sapImports.billingDocumentDetails.title')}
                description={t('sapImports.billingDocumentDetails.description')}
                isSnapshot
              />
              <SapImportTile
                uploadType="import-sap-customer-transactions"
                icon={<ArrowLeftRightIcon />}
                title={t('sapImports.customerTransactions.title')}
                description={t('sapImports.customerTransactions.description')}
                isSnapshot
              />
              <SapImportTile
                uploadType="import-sap-customer-partner-credit-segments"
                icon={<ShieldCheckIcon />}
                title={t('sapImports.creditSegments.title')}
                description={t('sapImports.creditSegments.description')}
              />
            </div>
          </PermissionsGuard>
        </CardContent>
      </Card>

      <PermissionsGuard required={['credit_duns_and_bradstreet_edit']}>
        <Card>
          <CardHeader>
            <CardTitle>{t('dunsAndBradstreet.title')}</CardTitle>
            <CardDescription>{t('dunsAndBradstreet.description')}</CardDescription>
          </CardHeader>

          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <SapImportTile
                uploadType="import-dnb"
                title={t('dunsAndBradstreet.import.title')}
                description={t('dunsAndBradstreet.import.description')}
              />
            </div>
          </CardContent>
        </Card>
      </PermissionsGuard>
    </PageContainer>
  )
}
